import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/common/Layout/Layout';
import ApiImage from '../../components/common/ApiImage';
import { BRAND } from '../../config/brand';
import { formatCurrency } from '../../utils/currency';
import './AccountOrders.css';

const BASE_URL     = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const SITE_URL     = BRAND?.url || process.env.REACT_APP_SITE_URL || 'https://theluviajewels.com';
const BRAND_NAME   = BRAND?.name || 'Luvia';
const ORDERS_PER_PAGE = 10;

/* ─────────────────────────────────────────────
   Constants
───────────────────────────────────────────── */
const STATUS_META = {
  pending:          { label: 'Pending',          color: 'gray'   },
  confirmed:        { label: 'Confirmed',         color: 'gold'   },
  processing:       { label: 'Processing',        color: 'blue'   },
  shipped:          { label: 'Shipped',           color: 'teal'   },
  out_for_delivery: { label: 'Out for Delivery',  color: 'amber'  },
  delivered:        { label: 'Delivered',         color: 'green'  },
  cancelled:        { label: 'Cancelled',         color: 'red'    },
  returned:         { label: 'Returned',          color: 'purple' },
};

/* Robust image resolution — handles every field shape the API might return */
const resolveImage = (item) =>
  item?.image?.url ||  // Backend stores as { url: string, alt: string }
  item?.productImage ||
  item?.image ||
  item?.images?.[0] ||
  item?.thumbnail ||
  item?.product?.images?.[0] ||
  null; // return null, not placeholder — we'll show a styled placeholder div

// Fallbacks mirrored from AccountOrderDetail — keeps list + detail consistent
const resolveTotal = (order) => {
  if (order.pricing?.total && order.pricing.total > 0) return order.pricing.total;
  if (order.total && order.total > 0) return order.total;
  const itemsTotal = (order.items || []).reduce(
    (sum, item) => sum + (item.price || 0) * (item.quantity || 1),
    0
  );
  return itemsTotal;
};

const resolveSubtotal = (order) => {
  if (order.pricing?.subtotal && order.pricing.subtotal > 0) return order.pricing.subtotal;
  if (order.subtotal && order.subtotal > 0) return order.subtotal;
  return resolveTotal(order);
};

const formatDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
};

/* ─────────────────────────────────────────────
   Skeleton
───────────────────────────────────────────── */
function Skeleton() {
  return (
    <div className="ord-skeleton" aria-hidden="true" aria-label="Loading orders">
      {[1, 2, 3].map((i) => (
        <div key={i} className="ord-skeleton__card">
          <div className="ord-skeleton__row">
            <div className="ord-skeleton__bar ord-skeleton__bar--60" />
            <div className="ord-skeleton__pill" />
          </div>
          <div className="ord-skeleton__bar ord-skeleton__bar--40" />
          <div className="ord-skeleton__items">
            {[1, 2].map((j) => (
              <div key={j} className="ord-skeleton__item">
                <div className="ord-skeleton__img" />
                <div className="ord-skeleton__iteminfo">
                  <div className="ord-skeleton__bar ord-skeleton__bar--80" />
                  <div className="ord-skeleton__bar ord-skeleton__bar--50" />
                </div>
              </div>
            ))}
          </div>
          <div className="ord-skeleton__footer">
            <div className="ord-skeleton__bar ord-skeleton__bar--30" />
            <div className="ord-skeleton__row ord-skeleton__row--gap">
              <div className="ord-skeleton__btn" />
              <div className="ord-skeleton__btn ord-skeleton__btn--wide" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────
   StatusBadge
───────────────────────────────────────────── */
function StatusBadge({ status }) {
  const meta = STATUS_META[status] || { label: status, color: 'gray' };
  return (
    <span className={`ord-badge ord-badge--${meta.color}`} aria-label={`Status: ${meta.label}`}>
      {meta.label}
    </span>
  );
}

/* ─────────────────────────────────────────────
   OrderCard
───────────────────────────────────────────── */
function OrderCard({ order, onReturn, onReorder }) {
  const canReturn = order.status === 'delivered';
  const orderNum = order.orderNumber || order._id?.slice(-8).toUpperCase();
  const dateISO = order.createdAt ? new Date(order.createdAt).toISOString().split('T')[0] : '';
  const subtotal = resolveSubtotal(order);
  const total    = resolveTotal(order);

  return (
    <article className="ord-card" aria-label={`Order #${orderNum}`}>
      {/* ── Header ── */}
      <div className="ord-card__head">
        <div className="ord-card__meta">
          <p className="ord-card__num">#{orderNum}</p>
          <time className="ord-card__date" dateTime={dateISO}>
            {formatDate(order.createdAt)}
          </time>
        </div>
        <StatusBadge status={order.status} />
      </div>

      {/* ── Items ── */}
      <div className="ord-card__items">
        {(order.items || []).map((item, idx) => {
          const img = resolveImage(item);
          const slug = item.slug || item.productSlug || item.product?.slug;
          const itemTotal = formatCurrency(item.price * (item.quantity || 1));

          return (
            <div key={idx} className="ord-item">
              {/* Image */}
              <div className="ord-item__img-wrap">
                {img ? (
                  <ApiImage
                    src={img}
                    fallback="/placeholder-jewelry.svg"
                    alt={item.name}
                    className="ord-item__img"
                    loading="lazy"
                  />
                ) : null}
                {/* Placeholder shown when img is null OR when img errors */}
                <div
                  className={`ord-item__img-placeholder${img ? '' : ' is-visible'}`}
                  aria-hidden="true"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>
                    <polyline points="21 15 16 10 5 21"/>
                  </svg>
                </div>
              </div>

              {/* Info */}
              <div className="ord-item__info">
                {slug ? (
                  <Link to={`/product/${slug}`} className="ord-item__name-link">
                    <p className="ord-item__name">{item.name}</p>
                  </Link>
                ) : (
                  <p className="ord-item__name">{item.name}</p>
                )}
                <p className="ord-item__attrs">
                  {[
                    item.size && `Size ${item.size}`,
                    item.color && item.color,
                    `Qty ${item.quantity || 1}`,
                  ].filter(Boolean).join(' · ')}
                </p>
              </div>

              {/* Price */}
              <p className="ord-item__price" aria-label={`Item total: ${itemTotal}`}>
                {itemTotal}
              </p>
            </div>
          );
        })}
      </div>

      {/* ── Footer ── */}
      <div className="ord-card__foot">
        {/* Subtotal + Total (mirrors detail page logic) */}
        <div className="ord-card__total-row">
          <span className="ord-card__total-label">Subtotal</span>
          <span className="ord-card__total-amount">{formatCurrency(subtotal)}</span>
        </div>
        <div className="ord-card__total-row ord-card__total-row--strong">
          <span className="ord-card__total-label">Order Total</span>
          <span className="ord-card__total-amount">{formatCurrency(total)}</span>
        </div>

        {/* CTA row */}
        <div className="ord-card__actions">
          {/* Reorder — primary conversion action */}
          <button
            className="ord-btn ord-btn--primary"
            onClick={() => onReorder(order)}
            aria-label={`Reorder items from order #${orderNum}`}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <polyline points="23 4 23 10 17 10"/>
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
            </svg>
            Reorder
          </button>

          {/* View Details */}
          <Link
            to={`/account/orders/${order._id}`}
            className="ord-btn ord-btn--ghost"
            aria-label={`View details for order #${orderNum}`}
          >
            View Details
          </Link>

          {/* Track Order — when we have tracking URL */}
          {order.trackingUrl && (
            <a
              href={order.trackingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="ord-btn ord-btn--ghost"
              aria-label={`Track order #${orderNum}`}
            >
              Track Order
            </a>
          )}

          {/* Return */}
          {canReturn && (
            <button
              className="ord-btn ord-btn--ghost"
              onClick={() => onReturn(order._id)}
            >
              Return
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

/* ─────────────────────────────────────────────
   Filter tabs
───────────────────────────────────────────── */
const FILTERS = [
  { id: 'all',       label: 'All' },
  { id: 'active',    label: 'Active' },
  { id: 'delivered', label: 'Delivered' },
  { id: 'cancelled', label: 'Cancelled' },
];

const matchesFilter = (order, filter) => {
  if (filter === 'all') return true;
  if (filter === 'active') return ['pending', 'confirmed', 'processing', 'shipped', 'out_for_delivery'].includes(order.status);
  return order.status === filter;
};

/* ─────────────────────────────────────────────
   Main Page
───────────────────────────────────────────── */
function AccountOrders() {
  const navigate = useNavigate();
  const { token, isAuthenticated, logout } = useAuth();

  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Math.max(1, parseInt(searchParams.get('page') || '1', 10));

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [retryCount, setRetryCount] = useState(0);
  const [pageMeta, setPageMeta] = useState({
    page: currentPage,
    totalPages: 1,
    total: 0,
  });

  /* ── Fetch with timeout and retry logic (server-side pagination) ── */
  const fetchOrders = useCallback(() => {
    if (!token) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    const controller = new AbortController();
    const TIMEOUT_MS = 30000; // Allow for backend cold start
    const MAX_RETRIES = 3;
    let timeoutId;

    const run = async (attempt = 0) => {
      setLoading(true);
      setError(null);

      timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

      try {
        const res = await fetch(
          `${BASE_URL}/account/orders?page=${currentPage}&limit=${ORDERS_PER_PAGE}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          }
        );
        clearTimeout(timeoutId);

        if (controller.signal.aborted || cancelled) return;

        // Handle 401 - session expired
        if (res.status === 401) {
          logout();
          navigate('/login', {
            replace: true,
            state: { reason: 'SESSION_EXPIRED' },
          });
          return;
        }

        if (!res.ok) {
          throw new Error(`Server error: ${res.status}`);
        }

        const data = await res.json();
        if (controller.signal.aborted || cancelled) return;

        if (data.success) {
          setOrders(data.orders || []);
          const pag = data.pagination || {};
          setPageMeta({
            page: pag.page ?? currentPage,
            totalPages: pag.totalPages ?? 1,
            total: pag.total ?? (data.orders || []).length,
          });
          setError(null);
          setRetryCount(0);
        } else {
          setOrders([]);
          setPageMeta((meta) => ({ ...meta, total: 0, totalPages: 1 }));
        }
      } catch (err) {
        clearTimeout(timeoutId);
        if (controller.signal.aborted || cancelled) {
          setError('Request timed out. Please check your connection and try again.');
        } else {
          if (attempt < MAX_RETRIES) {
            const delay = Math.min(1000 * Math.pow(2, attempt), 10000);
            console.warn(
              `[AccountOrders] Fetch failed, retrying in ${delay}ms (attempt ${attempt + 1}/${MAX_RETRIES})`
            );
            setTimeout(() => {
              if (!cancelled) {
                setRetryCount((prev) => prev + 1);
                run(attempt + 1);
              }
            }, delay);
            return;
          }

          setError(err.message || 'Failed to load orders. Please refresh the page.');
          if (process.env.NODE_ENV === 'development') {
            console.error('[AccountOrders] fetch error:', err);
          }
          setOrders([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    run(0);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [token, navigate, logout, currentPage]);

  useEffect(() => {
    const cleanup = fetchOrders();
    return () => {
      if (typeof cleanup === 'function') {
        cleanup();
      }
    };
  // retryCount is intentionally in deps so manual retries trigger re-fetch
  }, [fetchOrders, retryCount]);

  /* ── Reorder ── */
  const handleReorder = useCallback((order) => {
    navigate('/cart', { state: { reorderItems: order.items } });
  }, [navigate]);

  /* ── Return ── */
  const handleReturn = useCallback((orderId) => {
    navigate(`/account/orders/${orderId}/return`);
  }, [navigate]);

  /* ── Filtered list ── */
  const filteredOrders = orders.filter((o) => matchesFilter(o, activeFilter));

  /* ── Filter counts ── */
  const counts = FILTERS.reduce((acc, f) => {
    acc[f.id] = orders.filter((o) => matchesFilter(o, f.id)).length;
    return acc;
  }, {});

  /* ── Pagination handlers ─ */
  const handlePageChange = (nextPage) => {
    const target = Math.max(1, Math.min(nextPage, pageMeta.totalPages || 1));
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (target === 1) {
        next.delete('page');
      } else {
        next.set('page', String(target));
      }
      return next;
    });
  };

  return (
    <Layout showFooter={false} showBottomTab={true}>
      <Helmet>
        <title>My Orders | {BRAND_NAME}</title>
        <meta name="description" content={`Track and manage your ${BRAND_NAME} orders.`} />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href={`${SITE_URL}/account/orders`} />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
            { '@type': 'ListItem', position: 2, name: 'Account', item: `${SITE_URL}/account` },
            { '@type': 'ListItem', position: 3, name: 'My Orders', item: `${SITE_URL}/account/orders` },
          ],
        })}</script>
      </Helmet>
      
      <div className="ord-page">
        <div className="ord-container">

          {/* ── Page header ── */}
          <div className="ord-page-head">
            <Link to="/account" className="ord-back" aria-label="Back to Account">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M19 12H5M12 5l-7 7 7 7"/>
              </svg>
              Account
            </Link>
            <h1 className="ord-page-title">My Orders</h1>
            {!loading && pageMeta.total > 0 && (
              <p className="ord-page-sub">
                {pageMeta.total} order{pageMeta.total !== 1 ? 's' : ''} · Page {pageMeta.page} of {pageMeta.totalPages}
              </p>
            )}
          </div>

          {/* ── Filter tabs ── */}
          {!loading && !error && orders.length > 0 && (
            <div className="ord-filters" role="tablist" aria-label="Filter orders">
              {FILTERS.map((f) => (
                counts[f.id] > 0 || f.id === 'all' ? (
                  <button
                    key={f.id}
                    role="tab"
                    aria-selected={activeFilter === f.id}
                    className={`ord-filter-tab${activeFilter === f.id ? ' is-active' : ''}`}
                    onClick={() => setActiveFilter(f.id)}
                  >
                    {f.label}
                    {counts[f.id] > 0 && (
                      <span className="ord-filter-tab__count">{counts[f.id]}</span>
                    )}
                  </button>
                ) : null
                    ))}
                  </div>
          )}

          {/* ── Loading ── */}
          {loading && <Skeleton />}

          {/* ── Error ── */}
          {!loading && error && (
            <div className="ord-error" role="alert">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <p className="ord-error__msg">{error}</p>
              <button className="ord-btn ord-btn--ghost" onClick={fetchOrders}>Try Again</button>
                    </div>
          )}

          {/* ── Orders ── */}
          {!loading && !error && filteredOrders.length > 0 && (
            <div className="ord-list" role="feed" aria-label="Your orders" aria-busy={loading}>
              {filteredOrders.map((order, i) => (
                <OrderCard
                  key={order._id}
                  order={order}
                  onReturn={handleReturn}
                  onReorder={handleReorder}
                />
              ))}
            </div>
          )}

          {/* ── Pagination ── */}
          {!loading && !error && pageMeta.totalPages > 1 && (
            <nav className="ord-pagination" aria-label="Order pages">
              <button
                className="ord-btn ord-btn--ghost ord-pagination__btn"
                onClick={() => handlePageChange(pageMeta.page - 1)}
                disabled={pageMeta.page <= 1}
                aria-label="Previous page"
              >
                Previous
              </button>
              <span className="ord-pagination__info">
                Page {pageMeta.page} of {pageMeta.totalPages}
              </span>
              <button
                className="ord-btn ord-btn--ghost ord-pagination__btn"
                onClick={() => handlePageChange(pageMeta.page + 1)}
                disabled={pageMeta.page >= pageMeta.totalPages}
                aria-label="Next page"
              >
                Next
              </button>
            </nav>
          )}

          {/* ── No results for filter ── */}
          {!loading && !error && orders.length > 0 && filteredOrders.length === 0 && (
            <div className="ord-empty" role="status">
              <p className="ord-empty__title">No {FILTERS.find(f => f.id === activeFilter)?.label.toLowerCase()} orders</p>
              <button className="ord-btn ord-btn--ghost" onClick={() => setActiveFilter('all')}>Show all orders</button>
            </div>
          )}

          {/* ── Empty state ── */}
          {!loading && !error && orders.length === 0 && (
            <div className="ord-empty" role="status">
              <div className="ord-empty__icon" aria-hidden="true">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
                  <line x1="3" y1="6" x2="21" y2="6"/>
                  <path d="M16 10a4 4 0 01-8 0"/>
                </svg>
              </div>
              <p className="ord-empty__title">No orders yet</p>
              <p className="ord-empty__sub">Your first order will appear here once placed.</p>
              <Link to="/new-arrivals" className="ord-btn ord-btn--primary ord-btn--wide">
                Start Shopping
              </Link>
            </div>
          )}

        </div>
      </div>
      </Layout>
  );
}

export default AccountOrders;
